import React, { useRef, useLayoutEffect } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { makeStyles } from "@material-ui/core/styles";
import IconButton from "@material-ui/core/IconButton";
import clsx from "clsx";
import PickVarIcon from "./PickVarIcon";
import NodeIcon from "./NodeIcon";
import { resolveToken } from "./VarChip";

// Campo de texto com CHIPS inline (editável). Usa contenteditable: cada token
// {{…}} vira um "bloco" atômico (chip) no meio do texto; o resto é texto normal.
// Cada chip mostra o ÍCONE/COR do nó de origem + o rótulo. O valor ARMAZENADO
// continua sendo a string com {{…}} — serializa o DOM de volta; só re-renderiza
// quando o valor externo muda (preserva o cursor). Insere valores por "@" ou pelo
// ícone. `singleLine` = campo de uma linha (Enter não quebra).
const splitTokens = str => {
  const parts = [];
  const re = /\{\{\s*([^{}]+?)\s*\}\}/g;
  const s = String(str || "");
  let last = 0;
  let m;
  while ((m = re.exec(s))) {
    if (m.index > last) parts.push({ text: s.slice(last, m.index) });
    parts.push({ token: m[1] });
    last = m.index + m[0].length;
  }
  if (last < s.length) parts.push({ text: s.slice(last) });
  return parts;
};

const escapeHtml = s =>
  String(s || "").replace(/[&<>"]/g, c => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]));

const useStyles = makeStyles(theme => {
  const a = theme.app;
  return {
    label: { display: "block", fontSize: 12, fontWeight: 600, color: a.textSecondary, marginBottom: 5 },
    row: { position: "relative" },
    box: {
      border: `1px solid ${a.borderInput}`,
      borderRadius: 8,
      minHeight: 74,
      padding: "9px 38px 9px 11px",
      background: "#fff",
      fontSize: 13.5,
      lineHeight: 2,
      color: a.ink,
      cursor: "text",
      whiteSpace: "pre-wrap",
      wordBreak: "break-word",
      outline: "none",
      "&:focus": { borderColor: a.dark, boxShadow: "0 0 0 3px rgba(44,53,61,.08)" },
      "&:empty:before": { content: "attr(data-placeholder)", color: a.textTertiary },
      // chip do token (bloco atômico, não editável): ícone/cor do nó + rótulo
      "& [data-token]": {
        display: "inline-flex",
        alignItems: "center",
        gap: 5,
        verticalAlign: "middle",
        background: "#F1F3F6",
        border: `1px solid ${a.border}`,
        borderRadius: 7,
        padding: "1px 8px 1px 5px",
        margin: "0 2px",
        fontSize: 12.5,
        fontWeight: 600,
        lineHeight: 1.4,
        color: a.ink,
        userSelect: "none",
        whiteSpace: "nowrap",
      },
      // o ícone do nó dentro do chip fica com tamanho fixo e centralizado
      "& [data-token] > :first-child": { flexShrink: 0 },
    },
    single: { minHeight: 40, lineHeight: 1.9 },
    bolt: {
      position: "absolute",
      right: 5,
      top: "50%",
      transform: "translateY(-50%)",
      color: a.textTertiary,
      padding: 5,
      "&:hover": { color: a.ink },
    },
    hint: { fontSize: 11.5, color: a.textTertiary, marginTop: 5 },
  };
});

const TokenTextArea = ({ label, value, onChange, groups, placeholder, helperText, readOnly, onOpenPicker, singleLine }) => {
  const classes = useStyles();
  const ref = useRef(null);
  const groupsRef = useRef(groups);
  groupsRef.current = groups;
  const savedRange = useRef(null);

  const buildChip = inner => {
    const meta = resolveToken(inner, groupsRef.current);
    const span = document.createElement("span");
    span.setAttribute("contenteditable", "false");
    span.setAttribute("data-token", inner);
    const iconHtml = renderToStaticMarkup(
      <NodeIcon verbKey={meta.verbKey} triggerType={meta.triggerType} category={meta.category} color={meta.accent} size={15} chip />
    );
    span.innerHTML = `${iconHtml}<span>${escapeHtml(meta.label || inner)}</span>`;
    return span;
  };

  const renderInto = (root, val) => {
    root.innerHTML = "";
    splitTokens(val).forEach(p => {
      if (p.token != null) {
        root.appendChild(buildChip(p.token));
      } else {
        String(p.text).split("\n").forEach((ln, i) => {
          if (i > 0) root.appendChild(document.createElement("br"));
          if (ln) root.appendChild(document.createTextNode(ln));
        });
      }
    });
  };

  const serialize = root => {
    let out = "";
    const walk = node => {
      node.childNodes.forEach(ch => {
        if (ch.nodeType === 3) {
          out += ch.textContent;
        } else if (ch.nodeType === 1) {
          if (ch.hasAttribute && ch.hasAttribute("data-token")) out += `{{${ch.getAttribute("data-token")}}}`;
          else if (ch.tagName === "BR") out += "\n";
          else {
            if (/^(DIV|P)$/.test(ch.tagName) && out && !out.endsWith("\n")) out += "\n";
            walk(ch);
          }
        }
      });
    };
    walk(root);
    return out;
  };

  // sincroniza valor externo -> DOM SÓ quando difere (preserva o cursor ao digitar)
  useLayoutEffect(() => {
    if (!ref.current) return;
    if (serialize(ref.current) === (value || "")) return;
    renderInto(ref.current, value || "");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const emit = () => {
    if (ref.current) onChange(serialize(ref.current));
  };

  const captureRange = () => {
    const sel = window.getSelection();
    if (sel && sel.rangeCount && ref.current && ref.current.contains(sel.anchorNode)) {
      savedRange.current = sel.getRangeAt(0).cloneRange();
    }
  };

  // inserção de um token no cursor (usada pelo picker via onOpenPicker)
  const insertToken = inner => {
    const root = ref.current;
    if (!root) return;
    root.focus();
    const sel = window.getSelection();
    let range = null;
    if (savedRange.current && root.contains(savedRange.current.startContainer)) range = savedRange.current;
    else if (sel && sel.rangeCount && root.contains(sel.anchorNode)) range = sel.getRangeAt(0);
    if (!range) {
      range = document.createRange();
      range.selectNodeContents(root);
      range.collapse(false);
    }
    range.deleteContents();
    const chip = buildChip(inner);
    range.insertNode(chip);
    const space = document.createTextNode(" ");
    if (chip.nextSibling) chip.parentNode.insertBefore(space, chip.nextSibling);
    else chip.parentNode.appendChild(space);
    const nr = document.createRange();
    nr.setStart(space, space.length);
    nr.collapse(true);
    sel.removeAllRanges();
    sel.addRange(nr);
    savedRange.current = null;
    emit();
  };

  const openPicker = () => {
    captureRange();
    if (onOpenPicker) onOpenPicker(insertToken);
  };

  const onKeyDown = e => {
    if (e.key === "@") {
      e.preventDefault();
      openPicker();
    } else if (e.key === "Enter" && singleLine) {
      e.preventDefault();
    }
  };

  const onPaste = e => {
    e.preventDefault();
    const t = (e.clipboardData || window.clipboardData).getData("text/plain");
    document.execCommand("insertText", false, singleLine ? t.replace(/\n/g, " ") : t);
  };

  return (
    <div>
      {label && <span className={classes.label}>{label}</span>}
      <div className={classes.row}>
        <div
          ref={ref}
          className={clsx(classes.box, singleLine && classes.single)}
          contentEditable={!readOnly}
          suppressContentEditableWarning
          data-placeholder={placeholder || ""}
          onInput={emit}
          onBlur={emit}
          onKeyUp={captureRange}
          onMouseUp={captureRange}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
        />
        {!readOnly && (
          <IconButton
            size="small"
            tabIndex={-1}
            className={classes.bolt}
            title="Inserir um dado do fluxo"
            onMouseDown={e => e.preventDefault()}
            onClick={openPicker}
          >
            <PickVarIcon fontSize="small" />
          </IconButton>
        )}
      </div>
      {helperText && <div className={classes.hint}>{helperText}</div>}
    </div>
  );
};

export default TokenTextArea;
